import { totalItems, useBranchStore, useCart, useCustomerAuth, useRestaurantInfo } from '@agos/customer-core'
import { T, tt } from '@agos/shared'
import { FacebookIcon, InstagramIcon } from '@agos/ui'
import { ArrowLeft, ChevronDown, Clock, MapPin, Menu, Phone, ShoppingBag, User } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'wouter'

import { useBranches } from '../branch/use-branches'
import { useThemeName } from '../branding/use-theme-name'
import { DAY_LABELS, weekRows } from './opening-week'
import { LanguageSwitcher } from './LanguageSwitcher'

/**
 * The frame around every screen: header and footer.
 *
 * Ported from legacy's `Header.tsx` and `Footer.tsx` — the same layout, the same green account and cart
 * buttons, the same dark footer with its three columns. The first AGOS draft had a thin bordered bar and
 * a four-line footer, which is what the owner's 2026-07-31 inspection meant by *design does not match
 * legacy*.
 *
 * **Three headers, as legacy has.** `Header` picks between them on `data-theme`, because the difference
 * is structural and not a palette: `modern-minimal` centres the wordmark under a utility bar and puts
 * the navigation behind a menu button, and `cozy-bistro` stacks a tagline, a serif wordmark and round
 * chips under an accent ribbon. Every franchise in the dumps is on `classic`, so the other two are
 * reachable only by a settings change — which is exactly why leaving them out would have been invisible
 * until the day somebody made one.
 *
 * Everything below the header is shared: the three variants differ in the bar and nothing else, which is
 * also true of legacy.
 *
 * The restaurant's details come from `GET /settings/footer` through `useRestaurantInfo`, and every link
 * in them is passed through `safeLink` there — they are admin-authored, and a `javascript:` URL in the
 * Facebook field would run in the customer's session.
 */

export function Header() {
  const theme = useThemeName()
  if (theme === 'modern-minimal') return <ModernMinimalHeader />
  if (theme === 'cozy-bistro') return <CozyBistroHeader />
  return <ClassicHeader />
}

/** What every header needs: who is signed in, what the basket holds, and whether this is the checkout. */
function useHeaderFacts() {
  const info = useRestaurantInfo()
  const [location] = useLocation()
  const lines = useCart((state) => state.lines)
  const user = useCustomerAuth((state) => state.user)
  const count = totalItems(lines)

  // On the checkout, the logo becomes a way back. Legacy swaps them, and the reason holds: the one thing
  // a customer wants from that screen is out of it.
  const isCheckout = location === '/checkout'
  const profileLabel =
    user === null ? tt('Prihlásiť sa') : ((user.name ?? '').split(' ')[0] || tt('Profil'))
  const profileHref = user === null ? '/prihlasenie' : '/moj-profil'
  return { info, isCheckout, count, user, profileLabel, profileHref }
}

function ClassicHeader() {
  const { info, isCheckout, count, profileLabel, profileHref } = useHeaderFacts()

  return (
    <header
      className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-sm"
      data-header
    >
      <div className="max-w-5xl mx-auto px-4 flex items-center justify-between h-12 md:h-16">
        <div className="flex items-center gap-3">
          {isCheckout ? (
            <Link
              href="/"
              data-header-back
              className="p-3 -ml-3 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground active:bg-secondary/80 transition-colors inline-flex items-center justify-center min-w-[44px] min-h-[44px]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
          ) : (
            <Link href="/" className="flex items-center gap-2.5" data-header-home>
              {info.logoUrl === null ? (
                info.name !== '' && (
                  <span
                    className="font-display font-extrabold text-base md:text-lg text-foreground leading-tight"
                    data-restaurant-name
                  >
                    {info.name}
                  </span>
                )
              ) : (
                <img
                  src={info.logoUrl}
                  alt={info.name}
                  data-restaurant-name
                  className="h-8 md:h-10 w-auto object-contain"
                />
              )}
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          <BranchBadge />
          <Link
            href={profileHref}
            data-header-profile
            className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors group"
          >
            <User className="w-5 h-5" />
            <span className="text-sm font-semibold hidden sm:inline">{profileLabel}</span>
          </Link>
          <LanguageSwitcher variant="header" />
          {!isCheckout && (
            <Link
              href="/checkout"
              data-cart-icon
              className="relative p-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center justify-center"
            >
              <ShoppingBag className="w-7 h-7" />
              {count > 0 && (
                <span
                  data-cart-count
                  className="absolute top-0.5 right-0.5 bg-white text-green-700 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center z-20"
                >
                  {count}
                </span>
              )}
            </Link>
          )}
        </div>
      </div>
    </header>
  )
}


/**
 * `modern-minimal` — a utility bar, a centred wordmark, and the navigation behind a button.
 *
 * The layout is a three-column grid so the wordmark stays centred whatever is beside it; a flex row
 * would shift it every time the profile label changed length.
 */
function ModernMinimalHeader() {
  const { info, isCheckout, count, user, profileHref } = useHeaderFacts()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <header
      className="theme-mm-header sticky top-0 z-50 w-full bg-background border-b border-border"
      data-header
      data-header-theme="modern-minimal"
    >
      <div className="border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 h-7 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>
            <T>Otvorené</T>
          </span>
          <span className="hidden md:inline">
            <T>Doručujeme · Platba kartou · Bez registrácie</T>
          </span>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-3 items-center h-16">
        <div className="flex items-center gap-2 justify-self-start">
          <button
            type="button"
            data-header-menu
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label={tt('Menu')}
            className="p-2 -ml-2 rounded hover:bg-secondary text-foreground transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          {isCheckout && (
            <Link
              href="/"
              data-header-back
              className="p-2 rounded hover:bg-secondary text-foreground transition-colors inline-flex items-center gap-1 text-xs uppercase tracking-wider"
            >
              <ArrowLeft className="w-4 h-4" /> <T>Späť</T>
            </Link>
          )}
        </div>
        <div className="justify-self-center">
          <Link href="/" data-header-home className="block">
            {info.logoUrl === null ? (
              <span className="font-display font-bold text-lg tracking-tight" data-restaurant-name>
                {info.name}
              </span>
            ) : (
              <img src={info.logoUrl} alt={info.name} data-restaurant-name className="h-9 w-auto object-contain" />
            )}
          </Link>
        </div>
        <div className="flex items-center gap-1.5 justify-self-end">
          <BranchBadge />
          <Link
            href={profileHref}
            data-header-profile
            className="px-3 py-2 text-xs uppercase tracking-[0.12em] font-semibold text-foreground border border-border rounded hover:bg-secondary transition-colors hidden sm:inline-flex items-center gap-1.5"
          >
            <User className="w-3.5 h-3.5" /> {user === null ? <T>Účet</T> : (user.name ?? '').split(' ')[0]}
          </Link>
          <LanguageSwitcher variant="header" />
          {!isCheckout && (
            <Link
              href="/checkout"
              data-cart-icon
              className="relative px-3 py-2 text-xs uppercase tracking-[0.12em] font-semibold text-primary-foreground bg-primary rounded hover:opacity-90 transition-opacity flex items-center gap-1.5"
            >
              <ShoppingBag className="w-4 h-4" />
              <span data-cart-count>
                {count > 0 ? (
                  <>
                    {count} <T>v košíku</T>
                  </>
                ) : (
                  <T>Košík</T>
                )}
              </span>
            </Link>
          )}
        </div>
      </div>
      {menuOpen && (
        <div className="border-t border-border bg-background" data-header-menu-open>
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link href="/" className="hover:text-primary">
              <T>Domov</T>
            </Link>
            <Link href="/menu" className="hover:text-primary">
              <T>Ponuka</T>
            </Link>
            <Link href="/moje-objednavky" className="hover:text-primary">
              <T>Moje objednávky</T>
            </Link>
            <Link href="/moje-kupony" className="hover:text-primary">
              <T>Kupóny</T>
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}

/** `cozy-bistro` — an accent ribbon, a serif wordmark with a kicker under it, and round chips. */
function CozyBistroHeader() {
  const { info, isCheckout, count, user, profileHref } = useHeaderFacts()

  return (
    <header
      className="theme-cb-header sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md"
      data-header
      data-header-theme="cozy-bistro"
    >
      <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary" />
      <div className="max-w-6xl mx-auto px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p className="hidden md:block text-xs italic text-muted-foreground tracking-wide order-2 md:order-1">
          <T>Domáca chuť · Čerstvé suroviny</T>
        </p>
        <Link href="/" data-header-home className="block text-center order-1 md:order-2">
          {info.logoUrl === null ? (
            <span
              className="font-display text-2xl md:text-3xl font-semibold text-foreground tracking-tight"
              data-restaurant-name
            >
              {info.name}
            </span>
          ) : (
            <img
              src={info.logoUrl}
              alt={info.name}
              data-restaurant-name
              className="h-12 w-auto object-contain mx-auto"
            />
          )}
          <span className="block text-[10px] uppercase tracking-[0.4em] text-accent mt-0.5">
            <T>Reštaurácia</T>
          </span>
        </Link>
        <div className="flex items-center gap-2 justify-center md:justify-end order-3">
          {isCheckout ? (
            <Link
              href="/"
              data-header-back
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-medium hover:bg-secondary/80 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> <T>Naspäť</T>
            </Link>
          ) : (
            <>
              <BranchBadge />
              <Link
                href={profileHref}
                data-header-profile
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-medium hover:bg-secondary/80 transition-colors"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {user === null ? <T>Účet</T> : (user.name ?? '').split(' ')[0]}
                </span>
              </Link>
              <LanguageSwitcher variant="header" />
              <Link
                href="/checkout"
                data-cart-icon
                className="relative inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="hidden sm:inline">
                  <T>Košík</T>
                </span>
                {count > 0 && (
                  <span
                    data-cart-count
                    className="ml-1 inline-flex items-center justify-center min-w-[1.4rem] h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-[11px] font-bold"
                  >
                    {count}
                  </span>
                )}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

/**
 * The branch chip, and the one piece of chrome that is allowed to disappear: a brand with a single
 * branch has nothing to switch to, and legacy hides it rather than showing a control that does nothing.
 */
function BranchBadge() {
  const { branches, current } = useBranches()
  const open = useBranchStore((state) => state.open)
  if (branches.length < 2) return null

  return (
    <button
      type="button"
      onClick={open}
    data-branch-badge={current?.primaryDomain ?? ''}
      title={tt('Zmeniť pobočku')}
      aria-label={tt('Zmeniť pobočku')}
      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-full text-xs font-semibold text-foreground hover:bg-secondary transition-colors"
    >
      <MapPin className="w-4 h-4 text-primary shrink-0" />
      <span className="truncate max-w-[120px] hidden sm:inline">{current?.name ?? tt('Pobočka')}</span>
      <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
    </button>
  )
}

export function Footer({ cmsPages }: { cmsPages: Array<{ id: number; slug: string; title: string }> }) {
  const info = useRestaurantInfo()
  const { branches, current } = useBranches()
  const openPicker = useBranchStore((state) => state.open)

  const rows = weekRows(info.openingHours)
  const hasHours = info.openingHours?.days !== undefined && Object.keys(info.openingHours.days).length > 0
  const hasContact = info.phone !== null || info.address !== null
  const hasSocial = info.facebookUrl !== null || info.instagramUrl !== null
  if (!hasSocial && !hasContact && !hasHours && info.mapEmbedUrl === null && cmsPages.length === 0) {
    return null
  }

  const currentBranch = branches.length >= 2 ? current : null

  return (
    <footer className="bg-gray-900 text-gray-300 mt-16" data-footer>
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="space-y-4">
            <h3 className="text-white font-bold text-xl">{info.name}</h3>
            <p className="text-sm text-gray-400">
              <T>Objednajte si jedlo rýchlo a pohodlne.</T>
            </p>
            {hasSocial && (
              <div className="flex items-center gap-3 pt-2" data-footer-socials>
                {info.facebookUrl !== null && (
                  <a
                    href={info.facebookUrl}
                    data-footer-social="facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-blue-600 flex items-center justify-center transition-colors"
                  >
                    <FacebookIcon className="w-5 h-5 text-white" />
                  </a>
                )}
                {info.instagramUrl !== null && (
                  <a
                    href={info.instagramUrl}
                    data-footer-social="instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-pink-600 flex items-center justify-center transition-colors"
                  >
                    <InstagramIcon className="w-5 h-5 text-white" />
                  </a>
                )}
              </div>
            )}
          </div>

          {hasContact && (
            <div className="space-y-3">
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider">
                <T>Kontakt</T>
              </h4>
              {info.phone !== null && (
                <a
                  href={`tel:${info.phone.replace(/\s/g, '')}`}
                  data-footer-phone
                  className="flex items-center gap-3 text-sm hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  {info.phone}
                </a>
              )}
              {info.address !== null && (
                <div className="flex items-start gap-3 text-sm" data-footer-address>
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span className="whitespace-pre-line">{info.address}</span>
                </div>
              )}
            </div>
          )}

          {hasHours && (
            <div className="space-y-3" data-footer-hours>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" /> <T>Otváracie hodiny</T>
              </h4>
              <div className="space-y-1.5">
                {rows.map((row) => (
                  <div key={row.dayKeys[0]} className="flex justify-between text-sm gap-4">
                    <span className="text-gray-400">
                      {row.dayKeys.length > 1
                        ? `${DAY_LABELS[row.dayKeys[0]!] ?? ''} – ${DAY_LABELS[row.dayKeys.at(-1)!] ?? ''}`
                        : (DAY_LABELS[row.dayKeys[0]!] ?? '')}
                    </span>
                    <span className={row.hours === null ? 'text-red-400' : 'text-white font-medium'}>
                      {row.hours ?? tt('Zatvorené')}
                    </span>
                  </div>
                ))}
              </div>
              {info.openingHours?.emergencyClosed === true && (
                <p className="text-red-400 text-xs font-medium mt-2" data-footer-emergency>
                  <T>⚠ Dnes mimoriadne zatvorené</T>
                </p>
              )}
            </div>
          )}
        </div>

        {cmsPages.length > 0 && (
          <div className="mt-10 pt-8 border-t border-white/10" data-footer-cms>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              <T>Informácie</T>
            </h4>
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {cmsPages.map((page) => (
                <li key={page.id}>
                  <Link
                    href={`/page/${page.slug}`}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {info.mapEmbedUrl !== null && (
          <div className="mt-10 rounded-xl overflow-hidden border border-white/10" style={{ height: 220 }}>
            <iframe
              src={info.mapEmbedUrl}
              title={tt('Mapa')}
              width="100%"
              height="220"
              className="block"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}

        <div className="border-t border-white/10 mt-10 pt-6 space-y-3 text-center">
          {currentBranch !== null && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-x-3 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1.5 text-gray-400">
                <MapPin className="w-4 h-4 text-primary" /> <T>Pobočka:</T>
                <span className="text-white font-medium">{currentBranch.name}</span>
              </span>
              <button
                type="button"
                onClick={openPicker}
                data-footer-change-branch
                className="text-primary hover:text-white underline underline-offset-2 transition-colors font-medium"
              >
                <T>Zmeniť pobočku</T>
              </button>
            </div>
          )}
          {/* The year is read at render. Legacy does the same, and a hardcoded one is wrong every
              January. */}
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} {info.name}. <T>Všetky práva vyhradené.</T>
          </p>
        </div>
      </div>
    </footer>
  )
}
